import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * The admin session (§4.8). A separate guard on a separate table from the storefront, so nothing
 * here is shared with a customer session.
 *
 * `withCredentials` plus the XSRF header is Sanctum's SPA flow: the cookie is httpOnly and the
 * token travels in a header, so a token stolen from storage is not a thing that can exist.
 */
export interface AdminUser {
  id: number
  name: string
  email: string
  must_change_password: boolean
  totp_enabled: boolean
  totp_required: boolean
  roles: string[]
  permissions: string[]
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  withCredentials: true,
  withXSRFToken: true,
  headers: { Accept: 'application/json' },
})

export const useAuthStore = defineStore('auth', () => {
  const admin = ref<AdminUser | null>(null)
  const pending = ref(false)
  const error = ref<string | null>(null)
  const hydrated = ref(false)

  const isAuthenticated = computed(() => admin.value !== null)

  /*
   * Signed in, but the role demands two-factor and it is not set up yet. The API answers every
   * other call with 403 `totp_enrolment_required` (security audit 2026-10-01), so the router sends
   * the admin to the security page instead of letting each screen fail on its own.
   */
  const mustEnrol = computed(() => Boolean(admin.value?.totp_required && !admin.value?.totp_enabled))

  /** The menu is built from these, so a permission change needs no deploy. */
  function can(permission: string): boolean {
    return admin.value?.permissions.includes(permission) ?? false
  }

  function canAny(permissions: string[]): boolean {
    return permissions.some((permission) => can(permission))
  }

  async function csrf(): Promise<void> {
    const base = (import.meta.env.VITE_API_BASE ?? '').replace(/\/admin\/v1$/, '')
    await axios.get(`${base}/sanctum/csrf-cookie`, { withCredentials: true }).catch(() => {
      // A missing cookie surfaces as a 419 on the next call, which `login` already reports.
    })
  }

  async function fetchMe(): Promise<void> {
    try {
      const { data } = await api.get<{ data: AdminUser }>('/me')
      admin.value = data.data
    } catch {
      admin.value = null
    } finally {
      hydrated.value = true
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    pending.value = true
    error.value = null

    try {
      await csrf()
      const { data } = await api.post<{ data: AdminUser }>('/auth/login', { email, password })
      admin.value = data.data

      return true
    } catch (e) {
      error.value = messageFor(e)

      return false
    } finally {
      pending.value = false
    }
  }

  async function logout(): Promise<void> {
    await api.post('/auth/logout').catch(() => {})
    admin.value = null
  }

  /** The API always sends a Persian message with its code; never show a bare status. */
  function messageFor(e: unknown): string {
    if (axios.isAxiosError(e)) {
      const payload = e.response?.data as { error?: { message?: string } } | undefined

      return payload?.error?.message ?? 'ورود ناموفق بود.'
    }

    return 'ارتباط با سرور برقرار نشد.'
  }

  return { admin, pending, error, hydrated, isAuthenticated, mustEnrol, can, canAny, fetchMe, login, logout }
})
